import "reflect-metadata";
import {injectable} from "inversify";
import api, {parseServerError, processBlobErrorResponse} from "@dropDesk/data/clients/http.client";
import {
    ReportTicketAggregateEntity
} from "@dropDesk/domain/entities/reports/report_ticket_aggregate/report_ticket_aggregate.entity";
import {downloadFile, openFileInNewTab} from "@dropDesk/utils/helpers/files";
import {ReportEntityFilters} from "@dropDesk/presentation/components/filters/report_entity_filters";

@injectable()
export abstract class ReportTicketAggregateRemoteDataSource {
    public abstract get(period: { startDate: string, endDate: string }, filters?: ReportEntityFilters): Promise<ReportTicketAggregateEntity>;

    public abstract exportHtml(period: { startDate: string, endDate: string }, filters?: ReportEntityFilters): Promise<void>;
}

@injectable()
export class ReportTicketAggregateRemoteDataSourceImpl implements ReportTicketAggregateRemoteDataSource {
    baseUrl: string = 'reportTickets/aggregate/data';
    baseUrlExportHtml: string = 'reportTickets/aggregate/html';

    async get(period: { startDate: string; endDate: string }, filters?: ReportEntityFilters): Promise<ReportTicketAggregateEntity> {
        return new Promise<ReportTicketAggregateEntity>(async (resolve, reject) => {

            try {
                let url = `${this.baseUrl}?startDate=${period.startDate}&endDate=${period.endDate}`;
                if (filters?.idClient) {
                    url += `&idClient=${filters.idClient}`;
                }
                if (filters?.idContact) {
                    url += `&idContact=${filters.idContact}`;
                }
                if (filters?.idSector) {
                    url += `&idSector=${filters.idSector}`;
                }
                if (filters?.idUser) {
                    url += `&idUser=${filters.idUser}`;
                }
                if (filters?.idDescriptionStatic) {
                    url += `&idDescriptionStatic=${filters.idDescriptionStatic}`;
                }

                const response = await api.get(url);
                const reportTicketsAggregate = ReportTicketAggregateEntity.fromJson({
                    lineChartClientsAggregate: response.data.lineChartClientsAggregate,
                    lineChartSectorsAggregate: response.data.lineChartSectorsAggregate,
                    lineChartUsersAggregate: response.data.lineChartUsersAggregate,
                    lineChartDescriptionsStaticAggregate: response.data.lineChartDescriptionsStaticAggregate,
                    timeFirstResponseAggregate: response.data.timeFirstResponseAggregate,
                    timeResolutionAggregate: response.data.timeResolutionAggregate ?? 0,
                    totalTickets: response.data.totalTickets ?? 0,
                    averageRating: response.data.averageRating,
                    ticketsInBusinessHour: response.data.ticketsInBusinessHour ?? 0,
                    ticketsOutBusinessHour: response.data.ticketsOutBusinessHour ?? 0,
                    texts: response.data.texts,
                });
                return resolve(reportTicketsAggregate);

            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    async exportHtml(period: { startDate: string, endDate: string }, filters?: ReportEntityFilters): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                let url = `${this.baseUrlExportHtml}?startDate=${period.startDate}&endDate=${period.endDate}`;
                if (filters?.idClient) {
                    url += `&idClient=${filters.idClient}`;
                }
                if (filters?.idContact) {
                    url += `&idContact=${filters.idContact}`;
                }
                if (filters?.idSector) {
                    url += `&idSector=${filters.idSector}`;
                }
                if (filters?.idUser) {
                    url += `&idUser=${filters.idUser}`;
                }
                if (filters?.idDescriptionStatic) {
                    url += `&idDescriptionStatic=${filters.idDescriptionStatic}`;
                }
                const response = await api.get(url, {responseType: 'blob'});
                const file = new Blob([response.data], {type: 'text/html;charset=utf-8'});
                const fileURL = URL.createObjectURL(file);
                openFileInNewTab(file, 'Carregando relatório...');
                downloadFile(fileURL, `relatório_agregados_${period.startDate}-${period.endDate}.html`);
                return resolve();
            } catch (error) {
                await processBlobErrorResponse(error);
                return reject(parseServerError(error));
            }
        });
    }

}
